import { Button } from "../ui/Button"
import { useTranslation } from "react-i18next"
import { Dialog, DialogProps } from "@/components/ui/Dialog"
import { useState } from "react"
import clsx from "clsx"
import cullingUrl from "@assets/culling_small.png?url"
import editUrl from "@assets/edit_small.png?url"
import { OptionView } from "../ui/OptionView"
import { useAtomValue } from "jotai"
import { allPhotosAtom } from "@/store/photo/photos-state"
import { projectAtom } from "@/store/project/projects-state"

export type ProjectMode = "manual_edit" | "style" | "smart_cull"
export const SelectProjectModeDialog = (
	props: DialogProps & {
		onSelect: (mode: ProjectMode) => void
		trackingProjectId?: string
		trackingTotalPhotos?: number
	},
) => {
	const { t } = useTranslation("components")
	const [selectedEditMode, setSelectedEditMode] = useState<ProjectMode>("style")
	const project = useAtomValue(projectAtom)
	const allPhotos = useAtomValue(allPhotosAtom)
	const projectId = project?.id ?? props.trackingProjectId
	const totalPhotos =
		allPhotos.length > 0 ? allPhotos.length : (project?.photoCount ?? props.trackingTotalPhotos)
	const startCullingTrackingValues = {
		...(projectId ? { projectId } : {}),
		...(totalPhotos != null ? { totalPhotos } : {}),
	}
	const startEditingTrackingValues = projectId ? { projectId } : undefined

	const renderCullOption = () => {
		return (
			/*
				Change px-8 to px-[clamp(1rem,2.5vw,2rem)] py-[clamp(0.25rem,2.5vh,2rem)]
				This is to reduce padding when necessary; the scrollbar will only appear when the padding can no longer be reduced.
			*/
			<div
				className={clsx(
					"flex size-full flex-1 flex-col items-start justify-between space-y-4 rounded-lg bg-base-700 px-[clamp(1rem,2.5vw,2rem)] py-[clamp(0.25rem,2.5vh,2rem)]",
				)}
			>
				<div className="flex flex-col items-start space-y-2">
					<img
						src={cullingUrl}
						className="-ml-2 h-[clamp(56px,14vh,112px)] w-auto max-w-full object-contain"
					/>
					<div className="text-md font-bold">{t("selectModeDialog.cull")}</div>
					<div className="pb-2 text-sm text-base-400">
						{t("selectModeDialog.selectBestShots")}
					</div>
				</div>

				<Button
					className="w-full"
					trackingLabel="start_culling"
					aria-label={t("selectModeDialog.ariaStartCulling")}
					trackingValues={startCullingTrackingValues}
					variant="primary"
					onClick={() => {
						props.onSelect("smart_cull")
					}}
				>
					{t("selectModeDialog.startCulling")}
				</Button>
			</div>
		)
	}

	const renderEditOption = () => {
		return (
			<div
				className={clsx(
					"flex h-full flex-col items-start justify-between space-y-2 rounded-lg bg-base-700 px-[clamp(1rem,2.5vw,2rem)] py-[clamp(0.25rem,2.5vh,2rem)] text-start",
				)}
			>
				<div className="flex flex-col items-start space-y-2">
					<img
						src={editUrl}
						className="-ml-2 h-[clamp(56px,14vh,112px)] w-auto max-w-full object-contain"
					/>
					<div className="text-md font-bold">{t("selectModeDialog.edit")}</div>
					<div className="pb-2 text-sm text-base-400">
						{t("selectModeDialog.batchEditDescription")}
					</div>
					<OptionView
						id="ai"
						title={t("selectModeDialog.useAiStyle")}
						selected={selectedEditMode === "style"}
						handleSelect={() => setSelectedEditMode("style")}
					/>

					<OptionView
						id="manual"
						title={t("selectModeDialog.editManually")}
						selected={selectedEditMode === "manual_edit"}
						handleSelect={() => setSelectedEditMode("manual_edit")}
					/>
				</div>

				<Button
					className="w-full"
					trackingLabel="start_editing"
					aria-label={t("selectModeDialog.ariaStartEditing")}
					trackingValues={startEditingTrackingValues}
					variant="primary"
					onClick={() => {
						props.onSelect(selectedEditMode)
					}}
				>
					{t("selectModeDialog.startEditing")}
				</Button>
			</div>
		)
	}

	return (
		/*
			max-h-[min(720px,calc(100vh-2rem))]:
				This is to allow the layout engine to expand the height of the dialog at very low resolutions, thus avoiding component overlap.
				leaves 2rem total vertical margin.
		*/
		<Dialog
			modal
			contentSize="md"
			{...props}
			contentProps={{
				...props.contentProps,
				className: clsx(
					"max-h-[min(720px,calc(100vh-2rem))] overflow-y-auto",
					props.contentProps?.className,
				),
			}}
		>
			<div className="flex size-full min-h-0 flex-col space-y-4">
				<div className="grid size-full grid-cols-2 gap-4 pb-4">
					{renderCullOption()}
					{renderEditOption()}
				</div>
			</div>
		</Dialog>
	)
}

export const SelectEditModeDialog = (
	props: DialogProps & { onSelect: (mode: ProjectMode) => void },
) => {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<ProjectMode>("style")

	return (
		<Dialog
			canGoBack
			{...props}
			bottomButtons={
				<div className="flex w-[500px] flex-row items-center justify-end space-x-2">
					<Button
						trackingValue={selected}
						trackingLabel="continue"
						variant="primary"
						disabled={!selected}
						onClick={() => props.onSelect(selected)}
					>
						{t("common:action.continue")}
					</Button>
				</div>
			}
		>
			<div className="flex w-full flex-col space-y-4">
				<div className="text-start text-md font-bold">
					{t("selectModeDialog.selectEditMode")}
				</div>
				<div className="w-full text-start text-sm">
					{t("selectModeDialog.batchEditDescription")}
				</div>

				<div className="flex flex-col items-center space-y-2">
					<OptionView
						className="bg-transparent"
						id="ai"
						title={t("selectModeDialog.useAiStyle")}
						selected={selected === "style"}
						handleSelect={() => setSelected("style")}
					/>

					<OptionView
						className="bg-transparent"
						id="manual"
						title={t("selectModeDialog.editManually")}
						selected={selected === "manual_edit"}
						handleSelect={() => setSelected("manual_edit")}
					/>
				</div>
			</div>
		</Dialog>
	)
}
